import type {
  Alert,
  Phone,
} from "@tandemdiabetes/sugarmate-api-react-client"
import { useToasterContext } from "@tandemdiabetes/sugarmate-react-components"
import {
  useCallback,
  useMemo,
} from "react"

import { ActiveContacts } from "./ActiveContacts"
import styles from "./Contacts.module.scss"
import { PendingContacts } from "./PendingContacts"
import type { TestFunctionality } from "./TestFunctionality"
import { PhoneNumberModal } from "src/components"
import { usePolyglot } from "src/contexts"
import {
  usePhones,
  useCreatePhone,
  useUpdateAlert,
} from "src/hooks"
import {
  CreatePhoneErrorToast,
  AddAlertPhoneSuccessToast,
} from "src/toasts"
import {
  injectProps,
  getSugarmateError,
} from "src/utils"

const polyglotPrefix = "pages.alerts.contacts."

interface Props {
  // this hides the add button after maxNumberOfContacts is reached
  maxNumberOfContacts: number;
  sugarmateAlert: Alert;
  testFunctionality?: TestFunctionality;
}

export function Contacts(props: Props) {
  const {
    maxNumberOfContacts,
    testFunctionality,
    sugarmateAlert,
  } = props

  const polyglot = usePolyglot()
  const { showToast } = useToasterContext()
  const { data: phones } = usePhones()

  const updateAlert = useUpdateAlert()
  const createPhone = useCreatePhone()

  const [
    activePhones,
    pendingPhones,
  ] = useMemo(
    () => {
      const active: Phone[] = []
      const pending: Phone[] = []

      phones?.forEach(
        (phone: Phone) => {
          if (sugarmateAlert.phone_ids.includes(phone.id)) {
            if (phone.confirmed) {
              active.push(phone)
            } else {
              pending.push(phone)
            }
          }
        },
      )

      return [
        active,
        pending,
      ]
    },
    [
      sugarmateAlert,
      phones,
    ],
  )

  const onSubmit = useCallback(
    async (
      currentPhone: Phone,
      originalPhone: Phone,
      closeModal: () => void,
    ) => {
      if (currentPhone.phone_number === originalPhone.phone_number) {
        showToast(
          "add-alert-phone-success-toast",
          AddAlertPhoneSuccessToast,
        )

        closeModal()
        return
      }

      try {
        if (!createPhone || !updateAlert) {
          throw new Error("cannot edit phone")
        }

        // this finds a phone that already matches the form's phone_number value
        const matchingPhone = phones?.find((phone) => phone.phone_number === currentPhone.phone_number)

        // reference the matching phone id or create a phone with the new phone number
        const newPhoneId = matchingPhone?.id ?? (await createPhone({
          phone_number: currentPhone.phone_number,
          is_primary: false,
        })).data.phone.id

        // remove old phone id from phone_ids
        const phoneIds = sugarmateAlert.phone_ids.filter(
          (id: string) => id !== originalPhone.id,
        )

        // add new phone id to phone_ids
        phoneIds.push(newPhoneId)

        // update the alert
        await updateAlert({
          id: sugarmateAlert.id,
          phone_ids: phoneIds,
        })

        closeModal()

        showToast(
          "add-alert-phone-success-toast",
          AddAlertPhoneSuccessToast,
        )
      } catch (error) {
        const sugarmateError = getSugarmateError(error)

        const ErrorToast = injectProps(
          CreatePhoneErrorToast,
          {
            code: sugarmateError?.code,
            status: sugarmateError?.status,
          },
        )

        showToast(
          "add-alert-phone-error-toast",
          ErrorToast,
        )
      }
    },
    [
      showToast,
      createPhone,
      updateAlert,
      phones,
      sugarmateAlert,
    ],
  )

  return (
    <PhoneNumberModal onSubmit={onSubmit}>
      <ActiveContacts
        sugarmateAlert={sugarmateAlert}
        phones={activePhones}
        hideAddButton={sugarmateAlert.phone_ids.length >= maxNumberOfContacts}
        testFunctionality={testFunctionality}
      />
      <PendingContacts
        sugarmateAlert={sugarmateAlert}
        phones={pendingPhones}
      />
      <a
        className={styles.helpLink}
        id="getting-started"
        href="https://help.sugarmate.io/en/articles/4673352-setting-up-alerts"
        target="_blank"
        rel="noreferrer"
      >
        {polyglot.t(`${polyglotPrefix}help_link`)}
      </a>
    </PhoneNumberModal>
  )
}
